import { type FieldValues } from 'react-hook-form';

import { cn } from '@/shared/utils/cn/cn.util';

import { DynamicFormFields } from '../DynamicFormFields/DynamicFormFields';
import { FormFieldset } from '../FormFieldset/FormFieldset';
import { FORM_OBJECT_BODY_CLASSES } from '../dynamic-form.tokens';
import { fieldCellClasses } from '../resolve-field-layout/resolve-field-layout';

import { type ObjectFieldGroupProps } from './ObjectFieldGroup.types';
import { useObjectFieldGroupLogic } from './useObjectFieldGroupLogic';

/**
 * A value with a fixed shape, edited property by property.
 *
 * Three things it is deliberately NOT:
 *
 * 1. **Not a `Controller`.** An object is N controls, not one, so it never
 *    reaches `FieldRenderer` — `DynamicFormFields` branches to this instead.
 *    Each property is an ordinary field with an absolute dotted name, which is
 *    what gives every one of them its own dirty state, its own error, its own
 *    place in `shouldFocusError`, and a server 422 that lands on it.
 * 2. **Not its own grid implementation.** The body renders `DynamicFormFields`
 *    again — the same visibility filter, the same ramp, the same `data-columns`
 *    grip a section's grid has. A group is a small form, and there is one of
 *    those already.
 * 3. **Not a bordered card.** The kit's grouping mark is a legend over a
 *    hairline (`FormFieldset`); a box inside a box inside a scope column is
 *    three borders saying the same thing.
 *
 * The cell wrapper is rendered here rather than by the caller so a group carries
 * the same `data-field-name` handle as every other field — the walk measures
 * cells, and a field kind that is invisible to it is a field kind nothing checks.
 */
export function ObjectFieldGroup<TValues extends FieldValues>({
  field,
  control,
  columns,
  locale,
  values,
  pathFor,
  sourcePathFor,
  onSlugManualEdit,
  onAfterChange,
  onParseFailureChange,
}: ObjectFieldGroupProps<TValues>) {
  const { isInvalid, innerColumns, cellColSpan } = useObjectFieldGroupLogic({
    field,
    control,
    sectionColumns: columns,
  });

  return (
    <div
      data-field-name={field.name}
      data-col-span={String(cellColSpan)}
      data-object-group={field.name}
      className={cn(fieldCellClasses(field, columns))}
    >
      <FormFieldset
        // No id: a group's name is the same constant on every settings row
        // (`value`), so naming the element after it would give two scope columns
        // one DOM id. The cell above carries the walk's handle instead.
        title={field.label}
        isInvalid={isInvalid}
        {...(field.description === undefined ? {} : { description: field.description })}
      >
        <div data-form-container="" className={FORM_OBJECT_BODY_CLASSES}>
          <DynamicFormFields
            fields={field.properties}
            control={control}
            locale={locale}
            columns={innerColumns}
            values={values}
            pathFor={pathFor}
            sourcePathFor={sourcePathFor}
            onSlugManualEdit={onSlugManualEdit}
            onAfterChange={onAfterChange}
            onParseFailureChange={onParseFailureChange}
          />
        </div>
      </FormFieldset>
    </div>
  );
}
